/**
 * IubendaButton.tsx
 *
 * Questo componente si occupa di integrare Iubenda (cookie banner + consenso).
 * L’ho fatto in modo che il caricamento degli script avvenga solo una volta
 * e solo se `showBanner` è true.
 *
 * Parametri:
 * - siteId: ID del sito configurato in Iubenda
 * - cookiePolicyId: ID della cookie policy (Iubenda)
 * - showBanner: se true, carica e mostra effettivamente il banner
 *
 * Nota: se servirà modificare la configurazione del banner, il punto da toccare è
 * lo script `iubendaOptionsScript.innerHTML`. Qui dentro si settano tutte le opzioni.
 */

import React, { useEffect } from "react";
import { IUBENDA_API_KEY } from "../../config/appConfig";

interface IubendaProps {
  siteId: number;
  cookiePolicyId: number;
  showBanner: boolean;
}

const IubendaButton: React.FC<IubendaProps> = ({ siteId, cookiePolicyId, showBanner }) => {
  useEffect(() => {
    // Se non devo mostrare il banner, esco subito
    if (!showBanner) return;

    // Carico gli script solo se non sono già presenti
    if (!document.getElementById("iubenda-script")) {
      // Script con configurazione generale
      const iubendaOptionsScript = document.createElement("script");
      iubendaOptionsScript.innerHTML = `
          var _iub = _iub || [];
          _iub.csConfiguration = {
            "askConsentAtCookiePolicyUpdate": true,
            "floatingPreferencesButtonDisplay": "bottom-left",
            "perPurposeConsent": true,
            "reloadOnConsent": false,
            "siteId": ${siteId},
            "cookiePolicyId": ${cookiePolicyId},
            "lang": 'it',
            "banner": { 
              "acceptButtonDisplay": true,
              "closeButtonRejects": true,
              "customizeButtonDisplay": true,
              "explicitWithdrawal": true,
              "listPurposes": true,
              "prependOnBody": true
            }
          };
        `;
      document.head.appendChild(iubendaOptionsScript);

      // API KEY configuration
      const iubendaApiConfiguration = document.createElement("script");
      iubendaApiConfiguration.type = "text/javascript";
      iubendaApiConfiguration.textContent = `
        var _iub = _iub || {}; 
        _iub.cons_instructions = _iub.cons_instructions || []; 
        _iub.cons_instructions.push(["init", { api_key: "${IUBENDA_API_KEY}" }]);
      `;

      // Consent DB
      const consentDatabase = document.createElement("script");
      consentDatabase.id = "iubenda-consent";
      consentDatabase.src = "https://cdn.iubenda.com/cons/iubenda_cons.js";

      // Script principale
      const script = document.createElement("script");
      script.id = "iubenda-script";
      script.src = "//cdn.iubenda.com/cs/iubenda_cs.js";

      // Autoblocking
      const banner = document.createElement("script");
      banner.id = "iubenda-banner";
      banner.src = `https://cs.iubenda.com/autoblocking/${siteId}.js`;

      script.async = true;
      banner.async = true;
      document.body.appendChild(banner);
      document.body.appendChild(script);
      document.head.appendChild(consentDatabase);
      document.head.appendChild(iubendaApiConfiguration);
    }
  }, [siteId, cookiePolicyId, showBanner]);

  // Non renderizzo nulla (solo side-effect)
  return null;
};

export default IubendaButton;